import React, { useEffect, useState, useMemo } from "react"
import { Form } from "antd"
import { useLocation, Navigate, useNavigate, useSearchParams } from "react-router-dom"
import { isExpired } from "react-jwt"

// components
import { PrimeButton } from "components-design-system"
import Loading from "components/Loading/Loading"
import TextInput from "components-design-system/form-fields/TextInput/TextInput"
import ErrorNotification from "../ErrorNotification/ErrorNotification"

// api
import { postLogin } from "api/hooks/auth/useAuthApis"

// utilities
import useAuth from "context/hooks/useAuth"
import { getCookie } from "api/apiUtils"
import useRefreshToken from "utilities/useRefreshToken"
import useAppState from "context/hooks/useAppState"

// styles
import "./Login.scss"

const Login = () => {
  const [form] = Form.useForm()
  const [error, setError] = useState(null)
  const { auth, isUserAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { navigateToNextUrl, publicEnvVars } = useAppState()
  const { setTokens } = useRefreshToken()
  const [isPageLoading, setIsPageLoading] = useState(true)
  const [isSubmitLoading, setIsSubmitLoading] = useState(false)
  const location = useLocation()
  const pathname = location.pathname

  const saml = useMemo(() => {
    return searchParams.get("saml")
  }, [searchParams])

  useEffect(() => {
    if (saml === "true") {
      const access = getCookie("access")
      const refresh = getCookie("refresh")
      setTokens({ access, refresh })

      if (access && refresh && isExpired(access) === false && isExpired(refresh) === false) {
        navigateToNextUrl({ isSso: true })
      }
    } else {
      const isAuth = isUserAuthenticated()
      if (pathname.includes("login") && isAuth) {
        navigateToNextUrl()
      }
    }
    setIsPageLoading(false)
  }, [saml])

  useEffect(() => {
    const isAuth = isUserAuthenticated()
    if (searchParams.get("saml") !== "true" && pathname.includes("login") && isAuth) {
      navigateToNextUrl()
    }
  }, [])

  const renderSsoButtons = () => {
    if (publicEnvVars.SSO_URL && Object.keys(publicEnvVars.SSO_URL).length > 0) {
      return Object.keys(publicEnvVars.SSO_URL).map((key) => {
        const ssoProps = publicEnvVars.SSO_URL[key]
        return (
          <PrimeButton
            key={key}
            size="middle"
            href={publicEnvVars.SSO_URL[key].url}
          >
            {ssoProps?.img_url && (
              <img
                src={ssoProps.img_url}
                className="sso-button-img"
                alt={"logo"}
              />
            )}
            {ssoProps.button_text || "Login with SSO"}
          </PrimeButton>
        )
      })
    }
  }

  const handleLogin = async () => {
    const { email, password } = await form.getFieldValue()

    try {
      setIsSubmitLoading(true)
      const resp = await postLogin({
        email,
        password,
      })

      const access = resp?.data?.access
      const refresh = resp?.data?.refresh

      setTokens({
        access,
        refresh,
      })

      if (access && refresh) {
        navigateToNextUrl()
      }
      setError(null)
    } catch (error) {
      if (error.response?.data?.error?.msg) setError(error)
      else if (error?.response?.status === 302 && error?.response?.data?.reset_url) {
        setError(null)
        navigate("/password-reset", {
          state: {
            accessTempToken: error.response.data.access,
            isOldPasswordVisible: true,
          },
        })
      }
    } finally {
      setIsSubmitLoading(false)
    }
  }

  if (auth?.access) {
    return (
      <Navigate
        to="/"
        state={{ from: pathname }}
        replace={true}
      />
    )
  }

  if (isPageLoading) {
    return <Loading />
  }

  return (
    <div className="prime-main-login-container">
      <div className="right-container">
        <Form
          form={form}
          onFinish={handleLogin}
        >
          <div className="content-container">
            <div className="login-header">Welcome back!</div>
            {publicEnvVars.SSO_URL && Object.keys(publicEnvVars.SSO_URL).length > 0 && (
              <>
                <div className="sso-btns">{renderSsoButtons()}</div>
                <div className="break-container">
                  <span className="break-line"></span>
                  <span>&nbsp;&nbsp; or &nbsp;&nbsp;</span>
                  <span className="break-line"></span>
                </div>
              </>
            )}
            <div className="input-container">
              <Form.Item
                name="email"
                style={{ marginBottom: "0px" }}
                rules={[{ required: true }, { type: "email" }]}
              >
                <TextInput
                  type="text"
                  placeholder="E-mail"
                  prefix={<i className="fal fa-envelope login-input-icon" />}
                />
              </Form.Item>
              <Form.Item
                name="password"
                style={{ marginBottom: "0px" }}
                rules={[{ required: true }]}
              >
                <TextInput
                  type="password"
                  placeholder="Password"
                  prefix={<i className="fal fa-lock-alt login-input-icon" />}
                  style={{ marginTop: 12 }}
                />
              </Form.Item>
              <p className="forgot-password-link">
                <span
                  className="forgot-password-span"
                  onClick={() => navigate("/forgot-password")}
                >
                  Forgot password?
                </span>
              </p>
              <PrimeButton
                htmlType="submit"
                type="primary"
                className="login-btn"
                loading={isSubmitLoading}
              >
                Login
              </PrimeButton>
              <ErrorNotification error={error} />
            </div>
          </div>
        </Form>
      </div>
    </div>
  )
}

export default Login
